import React from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';

import type { MerchantExchangeRate, TraderExchangeRate } from '@/types/finance';
import ExchangeRatesTable from './ExchangeRatesTable';


export type IndividualExchangeRateRow = {
  id: string;
  owner_type: 'trader' | 'merchant';
  owner_id: string | null;
  owner_name: string;
  currency_id: string;
  currency?: { id: string; iso_code: string };
  rate: number | string;
  date_create?: string;
  date_update?: string | null;
  source: TraderExchangeRate | MerchantExchangeRate;
};

type Props = {
  rates: IndividualExchangeRateRow[];
  loading: boolean;
  error: string | null;
  onEdit: (item: IndividualExchangeRateRow) => void;
  onDelete: (item: IndividualExchangeRateRow) => void;
};

const formatRate = (value: number | string) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue)
    ? numericValue.toLocaleString('ru-RU', { maximumFractionDigits: 6 })
    : value;
};

const IndividualExchangeRates: React.FC<Props> = ({
  rates,
  loading,
  error,
  onEdit,
  onDelete,
}) => {
  const columns = [
    {
      field: 'currency',
      label: 'Валюта',
      render: (_value: unknown, row: IndividualExchangeRateRow) => row.currency?.iso_code || '-',
    },
    {
      field: 'owner_type',
      label: 'Тип',
      render: (value: IndividualExchangeRateRow['owner_type']) =>
        value === 'merchant' ? 'Мерчант' : 'Трейдер',
    },
    { field: 'owner_name', label: 'Получатель' },
    { field: 'rate', label: 'Курс', render: (value: number | string) => formatRate(value) },
    {
      field: 'date_update',
      label: 'Дата обновления',
      render: (value: string | null) => (value ? new Date(value).toLocaleString() : '-'),
    },
  ];

  return (
    <Box>
      <Typography variant="h5" mb={3}>Индивидуальные курсы</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <ExchangeRatesTable data={rates} columns={columns} onEdit={onEdit} onDelete={onDelete} />
      )}
    </Box>
  );
};

export default IndividualExchangeRates;
